import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CartStore } from './cart.store';
import { CartItem, CartItemGroup, CartItemType, ProjectCartItemDto, ProjectCartItemsReviewDto } from './cart.model';
import { environment } from '../../../../environments/environment';
import { map, tap } from 'rxjs/operators';
import { DefaultResponse } from '../session';
import { EncodeJobRequest, Project, ProjectsStore } from '../project';
import { Observable, of, take } from 'rxjs';
import { CartJobDto } from '../../../../../../api/src/encode/models/job-def.dto';
import { CreativeUnitPackageQuery } from '../ad/ad-unit-package.query';
import { CartQuery } from './cart.query';
import { Utils } from '../../../../../../api/src/project/project.utils';
import { CustomizationUtils } from '../../../../../../api/src/creative-unit-package/utils/customization.utils';
import { StoryFormat } from '../story-format';
import { uniq } from 'lodash';
import { CloudinaryImageTransformPipe } from '../../../shared/pipes/cloudinary-image-transform.pipe';
import { resolvePackageVersionStatus } from './cart-package-upgrade.utils';

@Injectable({ providedIn: 'root' })
export class CartService {
	constructor(
		private cartStore: CartStore,
		private cartQuery: CartQuery,
		private projectStore: ProjectsStore,
		private creativeUnitPackageQuery: CreativeUnitPackageQuery,
		private http: HttpClient
	) {}

	public get(projectId: Project['id'], listOnly?: boolean) {
		return this.http
			.get<DefaultResponse<CartItem[]>>(`${environment.apiUrl}/projects/${projectId}/cart-items${listOnly ? '/list' : ''}`)
			.pipe(
				map(response => {
					const pendingItems = this.cartQuery.getPendingItemsFromCart(projectId);

					// If there are pending items in the cart, we need to merge them with the response.
					if (pendingItems?.length) {
						return [...response?.data, ...pendingItems];
					}
					return response?.data;
				}),
				map(items => this.addPosition(items)),
				map(items => items.map(item => this.prepareForAkita(item))),
				tap(items => this.set(items))
			);
	}

	public fetchCartItems(projectId: Project['id'], listOnly = false): Observable<CartItem[]> {
		return this.http
			.get<DefaultResponse<CartItem[]>>(`${environment.apiUrl}/projects/${projectId}/cart-items${listOnly ? '/list' : ''}`)
			.pipe(
				map(response => {
					const data = response?.data ?? [];
					const pendingItems = this.cartQuery.getPendingItemsFromCart(projectId);
					return pendingItems?.length ? [...data, ...pendingItems] : data;
				}),
				map(items => this.addPosition(items)),
				map(items => items.map(item => this.prepareForAkita(item)))
			);
	}

	public getOne(projectId: Project['id'], cartItemId: CartItem['id'], dehydrated?: boolean) {
		return this.http
			.get<DefaultResponse<CartItem>>(
				`${environment.apiUrl}/projects/${projectId}/cart-items/${cartItemId}${dehydrated ? '/dehydrated' : ''}`
			)
			.pipe(
				map(response => response?.data),
				map(item => this.prepareForAkita(item)),
				tap(item => this.set([item]))
			);
	}

	public getReviewInfo(projectId: Project['id'], cartItemId: CartItem['id'], dehydrated?: boolean) {
		return this.http
			.get<DefaultResponse<CartItem>>(
				`${environment.apiUrl}/projects/${projectId}/cart-items/${cartItemId}/review${dehydrated ? '/dehydrated' : ''}`
			)
			.pipe(
				map(response => response?.data),
				tap(reviewInfo => this.cartStore.update(cartItemId, item => ({ ...item, ...reviewInfo })))
			);
	}

	public getVersion(projectId: Project['id'], cartItemId: CartItem['id']) {
		return this.http.get<DefaultResponse<CartItem>>(`${environment.apiUrl}/projects/${projectId}/cart-items/${cartItemId}`).pipe(
			map(response => response?.data),
			map(item => this.prepareForAkita(item))
		);
	}

	public add(projectId: Project['id'], itemDto: ProjectCartItemDto, skipHttp: boolean = false) {
		if (skipHttp) {
			const dirtyItem = { ...itemDto, dirty: true } as any;
			this.cartStore.add(this.prepareForAkita(dirtyItem as CartItem));
			return of(this.prepareForAkita(itemDto as CartItem));
		} else {
			return this.http
				.post<DefaultResponse<CartItem>>(
					`${environment.apiUrl}/projects/${projectId}/cart-items`,
					this.prepareForApi(itemDto as CartItem)
				)
				.pipe(
					map(response => response?.data),
					tap(item => this.cartStore.add(this.prepareForAkita(item)))
				);
		}
	}

	public update(
		projectId: Project['id'],
		id: CartItem['id'],
		itemDto: Partial<ProjectCartItemDto>,
		skipHttp: boolean = false,
		updateStore: boolean = false
	) {
		if (skipHttp) {
			const dirtyItem = { ...itemDto, dirty: true };
			return of(this.cartStore.update(id, dirtyItem));
		} else {
			return this.http
				.patch<DefaultResponse<CartItem>>(
					`${environment.apiUrl}/projects/${projectId}/cart-items/${id}`,
					this.prepareForApi(itemDto as CartItem)
				)
				.pipe(map(response => response?.data))
				.pipe(
					tap(item => {
						if (updateStore) {
							this.cartStore.update(id, this.prepareForAkita(item));
						}
					})
				);
		}
	}

	public remove(projectId: Project['id'], id: CartItem['id'], skipHttp: boolean = false) {
		if (!projectId || !id) {
			console.warn('CartService.remove called without projectId or id', { projectId, id });
			return of(null);
		}

		if (skipHttp) {
			return of(this.cartStore.remove(id));
		} else {
			return this.http.delete<DefaultResponse<{ id: string }>>(`${environment.apiUrl}/projects/${projectId}/cart-items/${id}`).pipe(
				map(response => response?.data),
				tap(item => {
					this.cartStore.remove(item.id);
					this.projectStore.update(projectId, project => ({
						...project,
						cartItems: project.cartItems?.filter(i => i.id !== item.id)
					}));
				})
			);
		}
	}

	public clear() {
		this.cartStore.set([]);
	}

	/**
	 * Full-replace the cart entity collection from an already-normalized snapshot
	 * (U8 in-place Discard). Unlike `set()`, this does NOT re-run
	 * `prepareCartItemWithPackage` / `prepareForAkita` — the caller hands rows that
	 * already underwent that normalization (the Discard baseline), so the restored
	 * store is byte-identical to the baseline and `hasChanges()` reads false. Active
	 * / editor / version pointers are the caller's responsibility to re-point.
	 */
	public replaceAll(items: CartItem[]) {
		this.cartStore.set(items || []);
	}

	/**
	 * Set (or clear) the transient `dirty` flag on a single store row WITHOUT going
	 * through the skipHttp `update` path (which unconditionally forces `dirty: true`).
	 * Used by U9's per-item Save reconciliation: a saved row is cleared (`false`), a
	 * failed row is kept dirty (`true`). Akita merges updates with the old entity,
	 * so clearing must explicitly overwrite `dirty` with `undefined`; omitting it
	 * retains the old flag. The cleared row serializes like the Discard baseline.
	 */
	public setDirty(id: CartItem['id'], dirty: boolean) {
		this.cartStore.update(id, item => ({ ...item, dirty: dirty ? true : undefined }));
	}

	public getItemCheckoutTable(projectId: Project['id'], id: CartItem['id']) {
		return this.http.get(`${environment.apiUrl}/projects/${projectId}/cart-items/${id}/checkout-table`).toPromise();
	}

	public requestPreview(projectId: Project['id'], id: CartItem['id'], request: EncodeJobRequest) {
		return this.http.post(`${environment.apiUrl}/projects/${projectId}/cart-items/${id}/request-preview`, request);
	}

	public submitCartJob(projectId: Project['id'], request: CartJobDto) {
		return this.http.post(`${environment.apiUrl}/projects/${projectId}/submit-cart-job`, request);
	}

	public addReview(projectId: Project['id'], cartItemsReviewDtos: ProjectCartItemsReviewDto, skipHttp: boolean = false) {
		if (skipHttp) {
			return of(this.cartStore.update(cartItemsReviewDtos.items));
		} else {
			return this.http
				.post<DefaultResponse<CartItem[]>>(`${environment.apiUrl}/projects/${projectId}/cart-items/review`, cartItemsReviewDtos)
				.pipe(
					map(response => response?.data),
					tap(items => {
						items = items.map(item => this.prepareCartItemWithPackage(item));
						this.cartStore.upsertMany(items?.map(item => this.prepareForAkita(item)));
					})
				);
		}
	}

	public set(items: CartItem[]) {
		this.cartStore.set(
			items?.map(item => {
				return this.prepareForAkita(this.prepareCartItemWithPackage(item));
			})
		);
	}

	public setActive(id: string) {
		this.cartStore.setActive(id);
		this.setEditorActiveId(id);
	}

	public setVersionPreview(version: CartItem) {
		if (!version?.isVersion) {
			return;
		}
		const item = this.cartQuery.getAll().find(item => item.sourceId === version.sourceId);

		this.setActive(item?.id || null);
		this.cartStore.update({ activeVersion: version });
	}

	public clearVersionPreview() {
		this.cartStore.update({ activeVersion: null });
	}

	/**
	 * Re-point the active version preview at a new source cart-item id. Used by U9's
	 * per-item Save reconciliation, which swaps a temp id → the server id.
	 *
	 * Deliberately NOT `setVersionPreview`: that also calls `setActive` and would
	 * clobber the active pointer. The object is rebuilt immutably because Akita
	 * deep-freezes the state tree on every entity op — an in-place write to
	 * `activeVersion` throws `TypeError: Cannot assign to read only property`.
	 */
	public repointVersionPreviewSource(sourceId: CartItem['id']) {
		const activeVersion = this.cartQuery.getValue().activeVersion;
		if (!activeVersion) {
			return;
		}
		this.cartStore.update({ activeVersion: { ...activeVersion, sourceId } });
	}

	public clearActive() {
		this.cartStore.setActive(null);
		this.clearEditorActiveId();
	}

	/** Guards against stacking one deferred group refresh per cart write. */
	private activeGroupRefreshQueued = false;

	public setActiveGroup(group: CartItemGroup) {
		this.cartStore.update({ activeGroup: group ? this.withResolvedCustomizations(group) : group });
	}

	/**
	 * Re-resolves the active group's customization tree against the current cart.
	 *
	 * Activation resolves the tree on its way through `setActiveGroup`; this is for the edits that
	 * reach the cart without re-activating the group (an agent apply, a relink, a bulk operation).
	 * A resolve that changes nothing leaves the stored group untouched, so this costs nothing while
	 * the members agree.
	 *
	 * Deferred past the current task, and coalesced, for two reasons:
	 *
	 * The previews pipeline calls this from inside a cart-store emission, and Akita delivers state
	 * synchronously. A write made during delivery reaches the subscribers already visited, and then the
	 * outer loop hands the subscribers after it the PRE-write state - so a late subscriber, the
	 * template's `activeGroup$ | async` among them, settles on the unresolved group while the store
	 * holds the resolved one. Writing after the delivery removes the nesting.
	 *
	 * And a group-wide edit writes one cart item per member, so this would otherwise run once per
	 * member; coalescing collapses that burst into a single resolve.
	 */
	public refreshActiveGroupCustomizations(): void {
		if (this.activeGroupRefreshQueued) {
			return;
		}

		this.activeGroupRefreshQueued = true;

		// Promise.resolve(), not queueMicrotask: zone.js patches it, so the write still happens inside
		// the Angular zone and the sidebar is change-detected.
		Promise.resolve().then(() => {
			this.activeGroupRefreshQueued = false;

			const group = this.cartQuery.getActiveGroup();

			if (group) {
				this.setActiveGroup(group);
			}
		});
	}

	/**
	 * A group carries one customization tree for a sidebar that has no active unit, and the group is
	 * built from a single member item (`createGroupFromItemDataGroup`), whose copy only speaks for the
	 * rows that unit itself shows. Resolve the tree against every member so each row shows the value of
	 * the unit that owns it.
	 *
	 * Resolved from the live cart rather than `group.items`: those are a snapshot taken when the group
	 * was built, so they go stale the moment a member item is edited.
	 */
	private withResolvedCustomizations(group: CartItemGroup): CartItemGroup {
		if (!group.items?.length) {
			return group;
		}

		const memberIds = new Set(group.items.map(item => item.id));
		const units = this.cartQuery
			.getAll()
			.filter(item => memberIds.has(item.id))
			.map(item => item.itemData)
			.filter(itemData => !!itemData);

		// A lone member still has to be resolved. The group's tree is a snapshot taken when the group was
		// built, not a live reference into that member, so it stops tracking the member the moment it is
		// edited. It costs nothing either: one unit cannot disagree with itself, so the divergence scan
		// finds nothing and the ownership pass is skipped.
		if (!units.length) {
			return group;
		}

		// Not every group arrives carrying a tree. Entering the studio straight on a group builds one by
		// spreading a cart item (`{ ...groupItems[0], id, items }` in the project page), which has no
		// `customizations` of its own, and the sidebar then falls back to that member's copy - the exact
		// staleness this resolve exists to remove. Fall back to the first live member's tree as the base
		// so the resolve happens regardless of how the group was assembled.
		const base = group.customizations?.length ? group.customizations : units[0].customizations;

		if (!base?.length) {
			return group;
		}

		const customizations = CustomizationUtils.mergeCustomizationValuesFromUnits(base, units);

		return customizations === group.customizations ? group : { ...group, customizations };
	}

	public clearActiveGroup() {
		this.cartStore.update({ activeGroup: null });
	}

	/**
	 * Sets the active Creative Unit state for the editor.
	 */
	public setEditorActiveId(id: string) {
		this.cartStore.update({ editor: { activeId: id } });
	}

	/**
	 * Clears the active Creative Unit state for the editor.
	 */
	public clearEditorActiveId() {
		this.cartStore.update({ editor: { activeId: null } });
	}

	public prepareForAkita(item: CartItem): CartItem {
		// Hygiene: Sync item ad unit values with the ad customizations.
		if (item.type !== CartItemType.AD_UNIT) return item;

		return {
			...item
			// itemData: CreativeUnitWithCustomizations.syncValuesWithCreativeUnit(item.itemData)
		};
	}

	public prepareForApi(item: CartItem): CartItem {
		return {
			...item,
			id: undefined,
			projectId: undefined
			// itemData: CreativeUnitWithCustomizations.syncValuesWithCustomizations(item.itemData)
		};
	}

	public convertItemsToGroups(items: CartItem[], groupBy: 'package' | 'creativeUnitListing' | 'both' = 'package'): CartItemGroup[] {
		let groups: CartItemGroup[] = [];
		// Group items
		// Separate out videos into their own group for each item.
		items.forEach(item => {
			if (item.type === CartItemType.VIDEO) {
				groups.push(this.convertVideoItemToGroup(item));
			} else if (item.type === CartItemType.AD_UNIT) {
				const groupData = item.itemData?.group || item.metadata?.group;

				// If the item is a creative unit listing with no group, let's skip this.
				if (groupBy === 'creativeUnitListing' && !groupData) {
					return;
				}

				let pkgIndex;

				// Check if a group already exists by the itemData packageId.
				if (groupBy === 'package' || groupBy === 'both') {
					pkgIndex = groups.findIndex(group => group.id === item.metadata?.packageId);
				}

				if (groupBy === 'package') {
					if (pkgIndex === -1) {
						groups.push(this.convertCreativeUnitItemToGroup(item));
					} else {
						groups[pkgIndex].items.push(item);
					}
				} else if (groupBy === 'creativeUnitListing') {
					// Check if a group already exists.
					let groupIndex = groups.findIndex(group => group.id === groupData?.id || group.id === groupData?.instanceId);

					if (groupIndex === -1) {
						groups.push(this.createGroupFromItemDataGroup(item));
					} else {
						groups[groupIndex].items.push(item);
					}
				} else if (groupBy === 'both') {
					if (pkgIndex === -1) {
						groups.push({
							...this.convertCreativeUnitItemToGroup(item),
							items: []
						});

						pkgIndex = groups.length - 1;
						groups[pkgIndex].subgroups = [];
					}

					if (groupData) {
						let subgroupIndex = groups[pkgIndex]?.subgroups?.findIndex(
							subgroup => subgroup.id === groupData?.id || subgroup.id === groupData?.instanceId
						);

						if (subgroupIndex === -1) {
							groups[pkgIndex].subgroups.push(this.createGroupFromItemDataGroup(item));
						} else {
							groups[pkgIndex].subgroups[subgroupIndex].items.push(item);
						}
					} else {
						groups[pkgIndex].items.push(item);
					}
				}
			}
		});

		return groups;
	}

	public combineItemsWithSubgroupItems(group: CartItemGroup) {
		const items = group?.items || [];
		const subgroups = group?.subgroups || [];

		if (!items?.length && !subgroups?.length) {
			return [];
		}

		const combinedGroups: CartItemGroup[] = [];

		if (items?.length) {
			items.forEach(i => {
				combinedGroups.push({
					id: i.id,
					parentType: 'package',
					parentGroupId: group.id,
					name: i.name,
					items: [i],
					thumbnailUrl: null,
					metadata: null,
					type: i.type
				});
			});
		}

		if (subgroups?.length) {
			subgroups.forEach(subgroup => {
				combinedGroups.push({
					...subgroup,
					parentGroupId: group.id,
					metadata: null
				});
			});
		}

		return combinedGroups;
	}

	convertVideoItemToGroup(item: CartItem): CartItemGroup {
		return {
			id: item.id,
			name: item.name,
			type: item.type,
			parentType: 'package',
			items: [item],
			thumbnailUrl: item.metadata?.thumbnailUrl,
			metadata: []
		};
	}

	convertCreativeUnitItemToGroup(item: CartItem): CartItemGroup {
		let thumbnailUrl: string | undefined;
		if (item.metadata.package?.thumbnail || item.metadata.thumbnail) {
			thumbnailUrl = new CloudinaryImageTransformPipe().transform(
				item.metadata.thumbnail?.assetPath || item.metadata.package?.thumbnail?.assetPath,
				{ width: 100, height: 100, mode: 'fill' }
			);
		}

		return {
			id: item.metadata.packageId,
			name:
				item.metadata.listingName ||
				item.metadata.listing?.name ||
				item.metadata.package?.name ||
				item.metadata.packageName ||
				item.metadata.packageId,
			type: item.type,
			parentType: 'package',
			parent: {
				id: item.metadata.packageId,
				...item.metadata.package
			},
			items: [item],
			thumbnailUrl,
			metadata: [],
			packageVersionStatus: resolvePackageVersionStatus(item)
		};
	}

	createGroupFromItemDataGroup(cartItem: CartItem): CartItemGroup {
		const groupData = cartItem.itemData?.group || cartItem.metadata?.group;

		return {
			id: groupData?.instanceId || groupData?.id,
			name: groupData?.name,
			type: CartItemType.AD_UNIT,
			parentType: 'group',
			parent: {
				id: cartItem.itemData?.packageId || cartItem.metadata.packageId,
				...cartItem.metadata?.package
			},
			items: [cartItem],
			thumbnailUrl: cartItem.metadata?.package?.thumbnail || cartItem.metadata?.thumbnail,
			metadata: [],
			locale: {
				group: cartItem.locale?.group,
				...groupData.locale
			},
			customizations: cartItem.itemData?.customizations || [], //values to show will also be updated in the disabledCustomization pipe
			packageVersionStatus: resolvePackageVersionStatus(cartItem)
		};
	}

	getReviewStatus(approved?: boolean, short?: boolean): string {
		if (approved === true) {
			return $localize`:Cart Item Review Status@@cartItemReviewStatusApprovedText:Approved`;
		} else if (approved === false) {
			return $localize`:Cart Item Review Status@@cartItemReviewStatusRejectedText:Rejected`;
		} else {
			if (short) {
				return $localize`:Cart Item Review Status@@cartItemReviewStatusPendingText:Pending`;
			} else return $localize`:Cart Item Review Status@@cartItemReviewStatusPendingReviewText:Pending Review`;
		}
	}

	getReviewStatusIcon(approved?: boolean): string {
		if (approved === true) {
			return 'done';
		} else if (approved === false) {
			return 'close';
		} else return 'schedule';
	}

	getReviewStatusClass(approved?: boolean): string {
		if (approved === true) {
			return 'approved';
		} else if (approved === false) {
			return 'rejected';
		} else return 'pending';
	}

	getReviewStatusCount(items: CartItem[], approved?: boolean) {
		if (approved === true) {
			return items?.filter(item => item.approved)?.length || 0;
		} else if (approved === false) {
			return items?.filter(item => item.approved === false)?.length || 0;
		} else return items?.filter(item => item.approved === null)?.length || 0;
	}

	getVideoStoryFormat(workspaceState: any) {
		return Utils.getProjectStoryFormatRaw(workspaceState);
	}

	getVideoFormatText(storyFormat: StoryFormat) {
		if (!storyFormat || !storyFormat.title) {
			return;
		}

		return (storyFormat.description || storyFormat.title)?.replace(/<br ?\/?>/g, ' ').trim();
	}

	getVideoResolution(storyFormat: StoryFormat) {
		if (!storyFormat || !storyFormat.canvasWidth || !storyFormat.canvasHeight) {
			return;
		}

		return storyFormat.canvasWidth + 'x' + storyFormat.canvasHeight;
	}

	setCartItemsFromProjectId(projectId: string) {
		this.get(projectId).pipe(take(1)).subscribe();
	}

	public prepareCartItemWithPackage(item: CartItem) {
		let foundPackage;
		const packageId = item.itemData?.packageId || item.metadata?.packageId;
		if (packageId) {
			foundPackage = this.creativeUnitPackageQuery.getValue().entities[packageId];

			if (foundPackage) {
				foundPackage = {
					id: foundPackage.id,
					name: foundPackage.name,
					tags: foundPackage.tags,
					thumbnail: foundPackage.thumbnail,
					exportTypes: foundPackage.exportTypes
				};
			}
		}
		return {
			...item,
			itemData: {
				...item.itemData,
				package: foundPackage
			},
			metadata: {
				...item.metadata,
				package: foundPackage
			}
		};
	}

	private addPosition(items: CartItem[]): CartItem[] {
		let packageIds = uniq(items.map(i => i.itemData?.packageId).filter(id => !!id));
		for (let packageId of packageIds) {
			let position: number = 0;
			let groupedByPackage = items.filter(i => i.itemData.packageId == packageId);
			if (groupedByPackage[0].itemData.position != undefined) break;
			let sortedByDimensions = groupedByPackage.sort(
				(a: CartItem, b: CartItem) => b.itemData.dimensions?.width - a.itemData.dimensions?.width
			);
			for (let sortedItem of sortedByDimensions) {
				let item = items.find(i => i.id == sortedItem.id);
				item.itemData.position = position;
				item['dirty'] = true;
				position++;
			}
		}

		return items;
	}
}
